/**
 * Background music: three looping beds and the transitions between them.
 *
 *   menu     → the start and game-over screens
 *   play     → a round in progress
 *   tension  → the closing seconds of the clock
 *
 * ## Making generated music loop
 *
 * Generated music has no seam-free loop point. Its first and last bars are
 * different musical moments, so `source.loop = true` on the raw file produces
 * an audible splice once every pass — and over a 180-second round the player
 * hears that splice three times and starts listening for it.
 *
 * The fix is to bake the crossfade into the buffer at load time rather than
 * schedule overlapping sources at play time. Given a buffer of length `D` and a
 * crossfade of `X`:
 *
 *   loop length L = D - X
 *   out[i] = in[i]                                        for i in [X, L)
 *   out[i] = in[i]·fadeIn(i/X) + in[L+i]·fadeOut(i/X)      for i in [0, X)
 *
 * The tail is mixed back over the head. Check the two joins:
 *
 * - Wrapping from `out[L-1]` to `out[0]`: `out[0]` is `in[L]` exactly, and
 *   `in[L-1] → in[L]` was contiguous in the source. Seamless.
 * - Leaving the crossfade at `out[X]`: that is `in[X]` exactly, and
 *   `in[X-1] → in[X]` was contiguous too. Seamless.
 *
 * So the result loops forever with one `AudioBufferSourceNode`, no scheduler,
 * no timers, and no drift. The curves are equal-power (sine/cosine) rather than
 * linear because the head and tail are uncorrelated signals, and a linear
 * crossfade between uncorrelated material dips about 3dB in the middle.
 *
 * ## Why this does not go through the voice pool
 *
 * `SynthBus.channel()` exists to allocate, steal and retire short voices. Music
 * is one node that must never be stolen and never retires, so it takes its own
 * path to the master gain — after the limiter, deliberately. Music has already
 * been loudness-normalised offline and should not be pumped by a compressor
 * reacting to a bonus stack. It ducks instead, which is a decision made here
 * with a known amount and a known recovery, rather than a side effect.
 */

import { PLAY_FILES, PlayRotation, defaultRotation } from '@/audio/play-rotation';

/** The three beds. */
export type MusicTrack = 'menu' | 'play' | 'tension';

/**
 * The file behind each bed. `play` has no single file: each round takes one of
 * {@link PLAY_FILES} from the rotation (`./play-rotation.ts`), so it is absent
 * here and resolved by the player.
 */
const FILES: Record<Exclude<MusicTrack, 'play'>, string> = {
  menu: 'music-menu',
  tension: 'music-tension',
};

/**
 * Seconds of tail folded back over the head to close the loop.
 *
 * Long enough to blend two unrelated bars, short enough that the folded region
 * stays a small fraction of a 30-60 second bed.
 */
const LOOP_CROSSFADE = 2;

/** Default seconds for a track change. Slow: nobody should notice the swap. */
const SWITCH_FADE = 1.8;

/** Default seconds for the fade at `stop()`. */
const STOP_FADE = 1.2;

/** Level the music bed sits at relative to the cues, linear. */
const MUSIC_TRIM = 0.55;

/**
 * How much louder the bed sits when the primary input is a finger.
 *
 * A phone plays through a speaker the size of a fingernail, usually somewhere
 * that was not chosen for listening. The bed is the first thing to disappear
 * under that: the cues survive because they are transient and limited, and the
 * music does not because it is neither. Desktop is left exactly as it was —
 * the mix was tuned there and there is nothing wrong with it.
 *
 * Applied to the bed alone rather than the master, so the cues keep their
 * relative weight and this reads as the music coming forward instead of the
 * whole game getting louder.
 *
 * There is room for it. The loudest bed peaks at -6.3 dBFS, so +1.94 dB leaves
 * it near -4.4 dBFS before the level and master gains — and that matters here
 * more than it looks, because music joins the graph *after* the limiter and is
 * the one path with nothing downstream to catch it.
 */
const MOBILE_MUSIC_BOOST = 1.25;

/**
 * Whether the primary input is a finger. Non-browser callers get `false`.
 *
 * The same test `quality.ts` and `layout.ts` each make. Not shared with them on
 * purpose: audio importing the renderer's layout module would be a worse
 * dependency than three duplicated lines. Worth extracting to a leaf module if
 * a fourth caller ever wants it.
 */
function coarsePointer(): boolean {
  const mm = globalThis.matchMedia?.bind(globalThis);
  if (typeof mm !== 'function') return false;
  try {
    return mm('(pointer: coarse)').matches;
  } catch {
    return false;
  }
}

/**
 * The bed's trim on this device.
 *
 * Takes the device test as an argument so both branches are reachable from a
 * test: under Node there is no `matchMedia`, so the mobile side would never be
 * exercised otherwise.
 */
export function musicTrim(coarse: boolean = coarsePointer()): number {
  return MUSIC_TRIM * (coarse ? MOBILE_MUSIC_BOOST : 1);
}

/** Where the bed sits while the game is paused, linear. Present, but behind the panel. */
const DIM_LEVEL = 0.35;

/** Smallest gain usable with an exponential ramp — it rejects zero. */
const FLOOR = 0.0001;

/**
 * Fold a buffer's tail back over its head so it loops without a seam.
 *
 * The fold is capped at a third of the buffer, so a truncated or unexpectedly
 * brief asset still loops — with a shorter blend — rather than throwing or
 * losing most of its length. A non-positive crossfade returns the input
 * untouched.
 */
export function makeLoopable(
  ctx: BaseAudioContext,
  input: AudioBuffer,
  crossfadeSeconds = LOOP_CROSSFADE,
): AudioBuffer {
  const rate = input.sampleRate;
  const fade = Math.min(Math.floor(crossfadeSeconds * rate), Math.floor(input.length / 3));
  if (fade <= 0) return input;

  const length = input.length - fade;
  if (length <= fade) return input;

  const output = ctx.createBuffer(input.numberOfChannels, length, rate);

  for (let channel = 0; channel < input.numberOfChannels; channel += 1) {
    const src = input.getChannelData(channel);
    const dst = output.getChannelData(channel);
    dst.set(src.subarray(0, length));

    for (let i = 0; i < fade; i += 1) {
      const u = i / fade;
      // Equal power: sin² + cos² = 1, so the sum holds level across the blend
      // even though head and tail are uncorrelated.
      const fadeIn = Math.sin((Math.PI / 2) * u);
      const fadeOut = Math.cos((Math.PI / 2) * u);
      dst[i] = src[i] * fadeIn + src[length + i] * fadeOut;
    }
  }

  return output;
}

interface Voice {
  track: MusicTrack;
  /** The file it is playing (for `play`, the round's variant). */
  file: string;
  source: AudioBufferSourceNode;
  gain: GainNode;
}

export interface MusicOptions {
  /** URL prefix for the assets. */
  base?: string;
  /** Overall music level, 0..1, on top of the built-in trim. */
  level?: number;
  /**
   * The bed's trim, linear, overriding the value derived from the device.
   * Present so tests can pin it; the game does not set it.
   */
  trim?: number;
  /** Which `play` variant each round takes; the game uses the stored rotation and the page's `?bgm=`. */
  rotation?: PlayRotation;
}

/**
 * Loads, loops and crossfades the background beds.
 *
 * Every method is safe to call before anything has loaded: requests are
 * remembered and applied once the buffer arrives, and a failed load leaves the
 * game silent-but-working rather than throwing.
 */
export class MusicPlayer {
  readonly #ctx: AudioContext;
  readonly #out: GainNode;
  readonly #duck: GainNode;
  readonly #dim: GainNode;
  /** Decoded and loop-folded beds, by file stem. */
  readonly #buffers = new Map<string, AudioBuffer>();
  readonly #loads = new Map<string, Promise<boolean>>();
  readonly #base: string;
  readonly #rotation: PlayRotation;
  /** The `play` variant this round is using, from its first `play` until the round's bed is released. */
  #roundVariant: number | null = null;

  #voice: Voice | null = null;
  /** What we have been asked to play, which may not have loaded yet. */
  #wanted: MusicTrack | null = null;
  #level: number;
  readonly #trim: number;
  #disposed = false;

  constructor(ctx: AudioContext, destination: AudioNode, options: MusicOptions = {}) {
    this.#ctx = ctx;
    this.#base = options.base ?? 'audio/music/';
    this.#level = options.level ?? 1;
    this.#trim = options.trim ?? musicTrim();
    this.#rotation = options.rotation ?? defaultRotation();

    // duck → dim → out → destination. Three nodes rather than one because all
    // three move independently and on different timescales: a duck is a 0.1s
    // reflex under a cue, a dim is a state that lasts as long as a pause panel,
    // and the level is a user setting. Sharing one gain would mean each of them
    // cancelling the others' scheduled ramps.
    this.#duck = ctx.createGain();
    this.#duck.gain.value = 1;
    this.#dim = ctx.createGain();
    this.#dim.gain.value = 1;
    this.#out = ctx.createGain();
    this.#out.gain.value = this.#level * this.#trim;
    this.#duck.connect(this.#dim);
    this.#dim.connect(this.#out);
    this.#out.connect(destination);
  }

  /** Hold the bed back behind a modal (the pause panel), or release it. */
  setDim(dimmed: boolean, ramp = 0.35): void {
    if (this.#disposed) return;
    const gain = this.#dim.gain;
    const now = this.#ctx.currentTime;
    gain.cancelScheduledValues(now);
    gain.setValueAtTime(Math.max(gain.value, FLOOR), now);
    gain.exponentialRampToValueAtTime(dimmed ? DIM_LEVEL : 1, now + Math.max(ramp, 0.01));
  }

  /** The bed currently playing, or the one requested but still loading. */
  get track(): MusicTrack | null {
    return this.#wanted;
  }

  get playing(): boolean {
    return this.#voice !== null;
  }

  /**
   * The file a bed plays now: `menu` and `tension` have one; `play` is this
   * round's variant, or the next round's when no round has claimed one yet.
   */
  #fileFor(track: MusicTrack): string {
    if (track !== 'play') return FILES[track];
    return PLAY_FILES[this.#roundVariant ?? this.#rotation.peek()] as string;
  }

  /**
   * Fetch and decode one bed. Idempotent, and never rejects.
   *
   * For `play` this loads only the variant that will play — the round's own, or
   * the next round's before one has started — never the whole rotation.
   *
   * @returns whether the bed is now playable.
   */
  load(track: MusicTrack): Promise<boolean> {
    return this.#loadFile(this.#fileFor(track));
  }

  /**
   * Fetch the `play` variant the NEXT round will take, so it is decoded before
   * the riffle. Call during the results screen. While a round is running the
   * rotation's head is already the next one; before any round it is the first.
   */
  preloadNext(): Promise<boolean> {
    return this.#loadFile(PLAY_FILES[this.#rotation.peek()] as string);
  }

  #loadFile(file: string): Promise<boolean> {
    let pending = this.#loads.get(file);
    if (!pending) {
      pending = this.#load(file);
      this.#loads.set(file, pending);
    }
    return pending;
  }

  async #load(file: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.#base}${file}.mp3`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const decoded = await this.#ctx.decodeAudioData(await res.arrayBuffer());
      this.#buffers.set(file, makeLoopable(this.#ctx, decoded));
      this.#evictPlay();
      return true;
    } catch (err) {
      // A failed fetch is not remembered: the next round asks again.
      this.#loads.delete(file);
      console.warn(`[audio] music "${file}" unavailable:`, err);
      return false;
    }
  }

  /**
   * Keep decoded `play` variants to the one sounding and the one queued. A
   * decoded minute of stereo is about 20 MB, and six of them held for a session
   * is a real cost on a phone for tracks that will not be heard again soon.
   */
  #evictPlay(): void {
    const keep = new Set<string>([PLAY_FILES[this.#rotation.peek()] as string]);
    if (this.#voice) keep.add(this.#voice.file);
    if (this.#roundVariant !== null) keep.add(PLAY_FILES[this.#roundVariant] as string);
    for (const file of PLAY_FILES) {
      if (keep.has(file)) continue;
      this.#buffers.delete(file);
      this.#loads.delete(file);
    }
  }

  /**
   * Crossfade to a bed, loading it first if necessary.
   *
   * Calling this with the bed that is already playing is a no-op, so it is safe
   * to drive from an event that fires repeatedly — `tick`, for instance.
   *
   * `play` claims the next variant from the rotation the first time it is asked
   * for in a round, and keeps it through `tension` (the same round); asking for
   * `menu` or stopping ends the round, so the next `play` takes a new one.
   */
  async play(track: MusicTrack, fade = SWITCH_FADE): Promise<void> {
    if (this.#disposed) return;
    if (this.#wanted === track && this.#voice?.track === track) return;

    if (track === 'menu') this.#roundVariant = null;
    if (track === 'play' && this.#roundVariant === null) this.#roundVariant = this.#rotation.take();
    const file = this.#fileFor(track);

    this.#wanted = track;
    if (!this.#buffers.has(file) && !(await this.#loadFile(file))) return;

    // An await happened; the player may have been stopped or redirected since.
    if (this.#disposed || this.#wanted !== track) return;
    if (this.#voice?.track === track && this.#voice.file === file) return;

    const buffer = this.#buffers.get(file);
    if (!buffer) return;

    const now = this.#ctx.currentTime;
    if (this.#voice) this.#retire(this.#voice, now, fade);

    const gain = this.#ctx.createGain();
    gain.gain.setValueAtTime(FLOOR, now);
    gain.gain.exponentialRampToValueAtTime(1, now + Math.max(fade, 0.01));
    gain.connect(this.#duck);

    const source = this.#ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(gain);
    source.start(now);

    this.#voice = { track, file, source, gain };
    this.#evictPlay();
  }

  /** Fade the bed out and release it. */
  stop(fade = STOP_FADE): void {
    this.#wanted = null;
    this.#roundVariant = null;
    if (!this.#voice) return;
    this.#retire(this.#voice, this.#ctx.currentTime, fade);
    this.#voice = null;
  }

  /**
   * Pull the music down under a loud cue, then bring it back.
   *
   * @param amountDb - positive decibels of attenuation.
   * @param hold - seconds at the ducked level before recovering.
   */
  duck(amountDb: number, hold: number): void {
    if (this.#disposed || !this.#voice) return;
    const gain = this.#duck.gain;
    const now = this.#ctx.currentTime;
    const target = Math.max(Math.pow(10, -Math.abs(amountDb) / 20), FLOOR);

    gain.cancelScheduledValues(now);
    gain.setValueAtTime(Math.max(gain.value, FLOOR), now);
    // Fast down, slow up — the shape every ducker uses, because the recovery is
    // what would otherwise be heard as pumping.
    gain.exponentialRampToValueAtTime(target, now + 0.08);
    gain.setValueAtTime(target, now + 0.08 + Math.max(hold, 0));
    gain.exponentialRampToValueAtTime(1, now + 0.08 + Math.max(hold, 0) + 0.7);
  }

  /** Music level, 0..1, independent of the master volume. */
  setLevel(level: number, ramp = 0.15): void {
    this.#level = Math.min(Math.max(level, 0), 1);
    const gain = this.#out.gain;
    const now = this.#ctx.currentTime;
    const target = Math.max(this.#level * this.#trim, FLOOR);
    gain.cancelScheduledValues(now);
    gain.setValueAtTime(Math.max(gain.value, FLOOR), now);
    gain.exponentialRampToValueAtTime(target, now + Math.max(ramp, 0.01));
  }

  get level(): number {
    return this.#level;
  }

  dispose(): void {
    this.#disposed = true;
    this.stop(0.05);
    this.#buffers.clear();
    this.#loads.clear();
    // Let the last fade finish before tearing the graph down.
    setTimeout(() => {
      try {
        this.#duck.disconnect();
        this.#dim.disconnect();
        this.#out.disconnect();
      } catch {
        /* already gone */
      }
    }, 200);
  }

  /** Fade a voice out and stop it once it is inaudible. */
  #retire(voice: Voice, now: number, fade: number): void {
    const seconds = Math.max(fade, 0.01);
    const gain = voice.gain.gain;
    gain.cancelScheduledValues(now);
    gain.setValueAtTime(Math.max(gain.value, FLOOR), now);
    gain.exponentialRampToValueAtTime(FLOOR, now + seconds);
    voice.source.stop(now + seconds + 0.05);
    voice.source.onended = () => {
      try {
        voice.source.disconnect();
        voice.gain.disconnect();
      } catch {
        /* already gone */
      }
    };
  }
}
